import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { scrollToSection } from '../utils/scrollToSection';
import '../styles/hero.css';

// Module-level cache: persists counter completion across remounts/re-renders
// so the counter animates once and stays at its final value.
const counterCache = {};

// AnimatedCounter defined outside Hero to prevent re-creation on parent re-renders.
// Uses requestAnimationFrame with real timestamps so it completes in exactly
// `duration` ms regardless of browser timer throttling (Lenis rAF, etc.).
const AnimatedCounter = ({ id, end, duration = 1800, suffix = '', showPlus = true }) => {
  const [count, setCount] = useState(() => (counterCache[id]?.done ? end : 0));
  const counterRef = useRef(null);

  useEffect(() => {
    // Already finished — show final value immediately, never re-animate
    if (counterCache[id]?.done) {
      setCount(end);
      return;
    }

    let rafId = null;
    let startTime = null;

    const step = (timestamp) => {
      if (startTime === null) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      // easeOutCubic for a premium deceleration
      const eased = 1 - Math.pow(1 - progress, 3);
      const value = Math.round(eased * end);
      setCount(value);

      if (progress < 1) {
        rafId = requestAnimationFrame(step);
      } else {
        setCount(end);
        counterCache[id] = { done: true };
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !counterCache[id]?.started) {
          counterCache[id] = { started: true, done: false };
          rafId = requestAnimationFrame(step);
        }
      },
      { threshold: 0.4 }
    );

    if (counterRef.current) {
      observer.observe(counterRef.current);
    }

    return () => {
      observer.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [id, end, duration]);

  return (
    <span ref={counterRef}>
      {showPlus && count > 0 && '+'}
      {count}
      {suffix}
    </span>
  );
};

const Hero = () => {
  const heroRef = useRef(null);

  const scrollToContact = () => scrollToSection('contacto');
  const scrollToServices = () => scrollToSection('servicios');

  return (
    <section id="inicio" className="hero" ref={heroRef}>
      <div className="hero-background">
        <div className="hero-image" />
        <div className="hero-overlay-horizontal"></div>
        <div className="hero-overlay-bottom"></div>
      </div>

      <div className="hero-container">
        <motion.div
          className="hero-content"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          <motion.div
            className="hero-badge"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <span className="badge-dot"></span>
            <span className="badge-text">Costa Blanca · Alicante, España</span>
          </motion.div>

          <motion.h1
            className="hero-title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
          >
            <span className="title-line-1">CONSTRUIMOS</span>
            <span className="title-line-2">TU VISIÓN</span>
          </motion.h1>

          <motion.p
            className="hero-description"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
          >
            Empresa integral de construcción, reformas y mantenimiento en la Costa Blanca.
            <br />
            Un solo número para todos los servicios de tu hogar y empresa.
          </motion.p>

          <motion.div
            className="hero-buttons"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8 }}
          >
            <button className="btn-glass btn-primary" onClick={scrollToContact} data-testid="hero-btn-presupuesto">
              SOLICITAR PRESUPUESTO
            </button>
            <button className="btn-glass btn-secondary" onClick={scrollToServices} data-testid="hero-btn-servicios">
              VER SERVICIOS
            </button>
          </motion.div>
        </motion.div>

        <motion.div
          className="hero-stats"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
        >
          <div className="stat-card">
            <div className="stat-number">
              <AnimatedCounter id="experiencia" end={20} suffix=" Años" />
            </div>
            <div className="stat-label">de Experiencia</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">
              <AnimatedCounter id="proyectos" end={500} suffix=" Proyectos" />
            </div>
            <div className="stat-label">Completados</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">
              <AnimatedCounter id="clientes" end={100} suffix="% Clientes" showPlus={false} />
            </div>
            <div className="stat-label">Satisfechos</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default React.memo(Hero);